/**
 * Mortgage Calculator Block
 * Interactive mortgage payment calculator with sliders for property price,
 * deposit, interest rate, and term.
 */

import { registerBlockRenderer, getBlockRendererContext } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';
import type { MortgageCalcData } from '../types';

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidMortgageData(data: unknown): data is MortgageCalcData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // All fields are optional with defaults, but validate types if present
  if (d.propertyPrice !== undefined && typeof d.propertyPrice !== 'number') return false;
  if (d.deposit !== undefined && typeof d.deposit !== 'number') return false;
  if (d.interestRate !== undefined && typeof d.interestRate !== 'number') return false;
  if (d.term !== undefined && typeof d.term !== 'number') return false;
  if (d.title !== undefined && typeof d.title !== 'string') return false;
  if (d.currency !== undefined && typeof d.currency !== 'string') return false;

  return true;
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderMortgageCalc(data: MortgageCalcData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('mortgage-calc');

  // Get currency from data, fall back to context, then to £
  const context = getBlockRendererContext();
  const currency = data.currency || context.currencySymbol || '£';
  const defaultPrice = data.propertyPrice || 425000;
  const defaultDeposit = data.deposit || 42500;
  const defaultRate = data.interestRate || 4.5;
  const defaultTerm = data.term || 25;

  let html = '';

  if (data.title) {
    html += `<h3 class="pms-mortgage-title">${escapeHtml(data.title)}</h3>`;
  }

  html += '<div class="pms-mortgage-calc">';

  // Property Price
  html += `
    <div class="pms-mortgage-field">
      <label class="pms-mortgage-label">Property Price</label>
      <div class="pms-mortgage-input-group">
        <span class="pms-mortgage-currency">${escapeHtml(currency)}</span>
        <input type="number" class="pms-mortgage-input" data-field="price" value="${defaultPrice}" min="10000" max="10000000" step="1000">
      </div>
      <input type="range" class="pms-mortgage-slider" data-slider="price" min="50000" max="2000000" step="5000" value="${defaultPrice}">
    </div>
  `;

  // Deposit
  html += `
    <div class="pms-mortgage-field">
      <label class="pms-mortgage-label">Deposit</label>
      <div class="pms-mortgage-input-group">
        <span class="pms-mortgage-currency">${escapeHtml(currency)}</span>
        <input type="number" class="pms-mortgage-input" data-field="deposit" value="${defaultDeposit}" min="0" max="5000000" step="1000">
      </div>
      <input type="range" class="pms-mortgage-slider" data-slider="deposit" min="0" max="500000" step="1000" value="${defaultDeposit}">
      <div class="pms-mortgage-ltv">LTV: <span data-display="ltv">90</span>%</div>
    </div>
  `;

  // Interest Rate
  html += `
    <div class="pms-mortgage-field">
      <label class="pms-mortgage-label">Interest Rate (%)</label>
      <div class="pms-mortgage-input-group">
        <input type="number" class="pms-mortgage-input" data-field="rate" value="${defaultRate}" min="0.1" max="15" step="0.1">
        <span class="pms-mortgage-suffix">%</span>
      </div>
      <input type="range" class="pms-mortgage-slider" data-slider="rate" min="1" max="10" step="0.1" value="${defaultRate}">
    </div>
  `;

  // Term
  html += `
    <div class="pms-mortgage-field">
      <label class="pms-mortgage-label">Mortgage Term</label>
      <div class="pms-mortgage-input-group">
        <input type="number" class="pms-mortgage-input" data-field="term" value="${defaultTerm}" min="5" max="40" step="1">
        <span class="pms-mortgage-suffix">years</span>
      </div>
      <input type="range" class="pms-mortgage-slider" data-slider="term" min="5" max="40" step="1" value="${defaultTerm}">
    </div>
  `;

  // Results
  html += `
    <div class="pms-mortgage-results">
      <div class="pms-mortgage-result-item pms-mortgage-result-main">
        <span class="pms-mortgage-result-label">Monthly Payment</span>
        <span class="pms-mortgage-result-value" data-display="monthly">${currency}0</span>
      </div>
      <div class="pms-mortgage-result-row">
        <div class="pms-mortgage-result-item">
          <span class="pms-mortgage-result-label">Loan Amount</span>
          <span class="pms-mortgage-result-value" data-display="loan">${currency}0</span>
        </div>
        <div class="pms-mortgage-result-item">
          <span class="pms-mortgage-result-label">Total Repayment</span>
          <span class="pms-mortgage-result-value" data-display="total">${currency}0</span>
        </div>
      </div>
      <div class="pms-mortgage-result-item pms-mortgage-result-interest">
        <span class="pms-mortgage-result-label">Total Interest</span>
        <span class="pms-mortgage-result-value" data-display="interest">${currency}0</span>
      </div>
    </div>
  `;

  html += '</div>';
  wrapper.innerHTML = html;

  // Get elements using data attributes (more robust than IDs)
  const priceInput = wrapper.querySelector('[data-field="price"]') as HTMLInputElement;
  const priceSlider = wrapper.querySelector('[data-slider="price"]') as HTMLInputElement;
  const depositInput = wrapper.querySelector('[data-field="deposit"]') as HTMLInputElement;
  const depositSlider = wrapper.querySelector('[data-slider="deposit"]') as HTMLInputElement;
  const rateInput = wrapper.querySelector('[data-field="rate"]') as HTMLInputElement;
  const rateSlider = wrapper.querySelector('[data-slider="rate"]') as HTMLInputElement;
  const termInput = wrapper.querySelector('[data-field="term"]') as HTMLInputElement;
  const termSlider = wrapper.querySelector('[data-slider="term"]') as HTMLInputElement;

  const ltvDisplay = wrapper.querySelector('[data-display="ltv"]') as HTMLElement;
  const monthlyDisplay = wrapper.querySelector('[data-display="monthly"]') as HTMLElement;
  const loanDisplay = wrapper.querySelector('[data-display="loan"]') as HTMLElement;
  const totalDisplay = wrapper.querySelector('[data-display="total"]') as HTMLElement;
  const interestDisplay = wrapper.querySelector('[data-display="interest"]') as HTMLElement;

  function formatCurrency(value: number): string {
    return currency + value.toLocaleString('en-GB', { maximumFractionDigits: 0 });
  }

  function calculate(): void {
    const price = parseFloat(priceInput.value) || 0;
    const deposit = parseFloat(depositInput.value) || 0;
    const rate = parseFloat(rateInput.value) || 0;
    const term = parseFloat(termInput.value) || 25;

    const loanAmount = Math.max(0, price - deposit);
    const ltv = price > 0 ? Math.round((loanAmount / price) * 100) : 0;

    // Monthly interest rate
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    // Monthly payment formula: M = P * [r(1+r)^n] / [(1+r)^n - 1]
    let monthlyPayment = 0;
    if (monthlyRate > 0 && numPayments > 0) {
      const factor = Math.pow(1 + monthlyRate, numPayments);
      monthlyPayment = loanAmount * (monthlyRate * factor) / (factor - 1);
    } else if (numPayments > 0) {
      monthlyPayment = loanAmount / numPayments;
    }

    const totalRepayment = monthlyPayment * numPayments;
    const totalInterest = totalRepayment - loanAmount;

    // Update displays
    ltvDisplay.textContent = String(ltv);
    monthlyDisplay.textContent = formatCurrency(Math.round(monthlyPayment));
    loanDisplay.textContent = formatCurrency(loanAmount);
    totalDisplay.textContent = formatCurrency(Math.round(totalRepayment));
    interestDisplay.textContent = formatCurrency(Math.round(totalInterest));
  }

  // Sync input and slider
  function syncInputSlider(input: HTMLInputElement, slider: HTMLInputElement): void {
    input.addEventListener('input', () => {
      slider.value = input.value;
      calculate();
    });
    slider.addEventListener('input', () => {
      input.value = slider.value;
      calculate();
    });
  }

  syncInputSlider(priceInput, priceSlider);
  syncInputSlider(depositInput, depositSlider);
  syncInputSlider(rateInput, rateSlider);
  syncInputSlider(termInput, termSlider);

  // Initial calculation
  calculate();

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('mortgage-calc', (data: unknown, container: HTMLElement) => {
  if (!isValidMortgageData(data)) {
    console.warn('[mortgage-calc] Invalid data:', data);
    const wrapper = createBlockWrapper('mortgage-calc');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid mortgage calculator data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderMortgageCalc(data, container);
});
